import type { ReliabilityBreakdown } from '../../lib/benchmarks';
import { fmtCallSample, pct } from '../../lib/format';
import { m } from '../../paraglide/messages';

export interface ReliabilityMeterProps {
  /** share of all calls that returned data, percent (the headline figure) */
  successPct: number;
  /** uptime population; absent while reading the old gateway contract */
  sample?: number;
  breakdown: ReliabilityBreakdown;
}

/**
 * Tiny floor (percent of the bar) so a nonzero segment renders as a couple of
 * pixels instead of collapsing to 0px. Small enough that the bar stays honest
 * by construction: segments are otherwise drawn strictly proportional to their
 * true shares (widths are renormalized to sum to 100 after the floor), so no
 * "magnified" disclaimer is needed. The legend carries the exact percentages.
 */
const FLOOR = 0.6;

interface Segment {
  key: string;
  color: string;
  /** true share, percent (shown in the legend) */
  pct: number;
  /** rendered width, percent (floored then renormalized to sum to 100) */
  width: number;
  label: string;
}

const swatch = (color: string) => (
  <span className="size-2.5 shrink-0 rounded-2" style={{ background: color }} />
);

/**
 * Concept A reliability meter: the headline success rate over a slim segmented
 * bar (succeeded on first lane / recovered by failover / failed) with a legend
 * carrying the true percentages. The bar is proportional at every tail size,
 * from ~99.5% success down to a struggling SKU with large failover/failed
 * shares; zero segments are dropped, and the rounded container clips only the
 * outer corners so internal joins stay square.
 */
export const ReliabilityMeter = ({ successPct, sample, breakdown }: ReliabilityMeterProps) => {
  const { firstLanePct, failoverPct, failedPct } = breakdown;

  const raw = [
    { key: 'first', color: 'var(--accent)', pct: firstLanePct, label: m.measure_reliability_first_lane() },
    { key: 'failover', color: 'var(--c-amber)', pct: failoverPct, label: m.measure_reliability_failover() },
    { key: 'failed', color: 'var(--neg)', pct: failedPct, label: m.measure_reliability_failed() },
  ].filter((s) => s.pct > 0);

  const floored = raw.map((s) => Math.max(s.pct, FLOOR));
  const total = floored.reduce((n, w) => n + w, 0) || 1;
  const segments: Segment[] = raw.map((s, i) => ({ ...s, width: (floored[i] / total) * 100 }));

  return (
    <div>
      <div className="text-34 font-semibold leading-none tracking-[-0.02em] text-text">
        {pct(successPct)}
      </div>
      <div className="mt-1 text-12 font-medium text-text-2">
        {sample !== undefined
          ? m.measure_reliability_caption_sample({ sample: fmtCallSample(sample) })
          : m.measure_reliability_caption()}
      </div>

      <div
        role="img"
        aria-label={m.measure_reliability_aria({ success: pct(successPct), failed: pct(failedPct) })}
        className="mt-3.5 flex h-3 w-full overflow-hidden rounded-full bg-surface-3"
      >
        {segments.map((s) => (
          <span key={s.key} style={{ width: `${s.width}%`, background: s.color }} />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-12 text-text-2">
            {swatch(s.color)}
            {s.label} {pct(s.pct)}
          </span>
        ))}
      </div>
    </div>
  );
};
